import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { GraduationCap, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) { toast.success("Welcome back"); navigate("/"); }
    else toast.error(r.error || "Login failed");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual panel */}
      <div className="hidden lg:flex bg-slate-950 text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "24px 24px"
        }} />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 bg-emerald-500 text-slate-950 flex items-center justify-center rounded-sm">
            <GraduationCap size={22} strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-2xl font-black tracking-tight">Edurise</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Sales CRM</div>
          </div>
        </div>
        <div className="relative space-y-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400">赋能教育者 · The Command Center</div>
          <h1 className="font-display text-5xl font-black leading-[1.05] tracking-tighter">
            让每一位<br/>
            <span className="text-emerald-400">教育者的销售</span><br/>
            都不再手忙脚乱。
          </h1>
          <p className="text-slate-400 max-w-md leading-relaxed">
            所有渠道 (WhatsApp · Messenger · Instagram) 咨询集中在一个收件箱。AI 自动草拟回复,
            清晰的销售管道,让你的 4 人团队专注在真正会成交的学员上。
          </p>
          <div className="grid grid-cols-3 gap-4 max-w-md pt-4 border-t border-slate-800">
            <div><div className="font-display text-3xl font-black text-emerald-400">1</div><div className="text-xs text-slate-500 mt-1">统一收件箱</div></div>
            <div><div className="font-display text-3xl font-black text-orange-400">AI</div><div className="text-xs text-slate-500 mt-1">草拟回复</div></div>
            <div><div className="font-display text-3xl font-black">∞</div><div className="text-xs text-slate-500 mt-1">课程与线索</div></div>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">EduRISE Malaysia · app.edurise.com · Feb 2026</div>
      </div>

      {/* Right form */}
      <div className="flex items-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-700 font-semibold">登入 · Sign in</div>
          <h2 className="font-display text-4xl font-black tracking-tight mt-2 mb-2">欢迎回来</h2>
          <p className="text-slate-500 mb-8">输入你的账号密码,打开销售驾驶舱。</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-emerald-700 focus:ring-2 focus:ring-emerald-100"
                required
              />
            </div>
            <button
              data-testid="login-submit-btn"
              disabled={busy}
              className="w-full py-3.5 bg-slate-950 text-white text-sm font-semibold rounded-sm hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
